/**
 * @module useStudentTermOptions
 * @description Term-selector state for the student portal. Builds the dropdown
 * options from the calendars the logged-in student has an academic record for
 * (`Student.academicCalendars`, the `student_academic_calendar` pivot) — not the
 * school's full calendar history — newest first, and defaults the selection to
 * the school's current (`is_active`) calendar, falling back to the student's
 * most recent calendar when they have no record in the current one.
 *
 * @param academicCalendars - Ref array of the student's academic calendars
 *
 * @returns
 * - `calendars`: The student's calendars, newest first, unusable entries dropped
 * - `termOptions`: `{ label, value }` options for the term dropdown
 * - `selectedTerm`: Currently selected academic calendar id
 * - `onTermSelect`: Select handler (undefined clears the selection)
 *
 * @used_by
 * - StudentDashboard/Result/ViewResults/ViewResults.vue
 */
import type { AcademicCalendar, Maybe } from '@/types/composition-functions';

export function useStudentTermOptions(
  academicCalendars: Ref<Maybe<AcademicCalendar>[]>,
) {
  const selectedTerm = ref<string>();

  // Newest first; the relation itself carries no ordering. started_at is a
  // "Y-m-d H:i:s" string, so lexicographic comparison is chronological.
  const calendars = computed(() =>
    (academicCalendars.value || [])
      .filter((c): c is AcademicCalendar => !!c?.id && !!c.termStructure?.name)
      .sort((a, b) => {
        if (!a.started_at && !b.started_at) return 0;
        if (!a.started_at) return 1;
        if (!b.started_at) return -1;
        return String(b.started_at).localeCompare(String(a.started_at));
      }),
  );

  const termOptions = computed(() =>
    calendars.value.map((c) => ({
      label: `${c.session?.name} - ${c.termStructure?.name}`,
      value: c.id,
    })),
  );

  // Default to the school's current calendar; a student with no record in it
  // (e.g. not yet enrolled this term) gets their most recent calendar instead.
  watch(calendars, (list) => {
    if (selectedTerm.value) return;
    const mostRecent = list[0];
    if (!mostRecent) return;
    const current = list.find((c) => c.is_active);
    selectedTerm.value = (current ?? mostRecent).id;
  }, { immediate: true });

  function onTermSelect(value: string | undefined) {
    selectedTerm.value = value;
  }

  return { calendars, termOptions, selectedTerm, onTermSelect };
}
